import { useState } from 'react';
import { accountsApi } from '../../api/services';
import { STATEMENT_SECTIONS, type ClassificationDto, type LevelDto } from '../../api/types';
import {
  Alert, Badge, Button, Card, Field, Form, Input, Loadable, Modal, NumberInput, PageHeader, Select, Table, useAction, useAsync,
} from '../../ui/components';
import { PageHelp } from '../../ui/help';

/** Niveles de cuenta y clasificaciones (con su sección para estados financieros). */
export function StructurePage() {
  const levels = useAsync(() => accountsApi.levels(), []);
  const classes = useAsync(() => accountsApi.classifications(), []);
  const [level, setLevel] = useState<LevelDto | 'new' | null>(null);
  const [cls, setCls] = useState<ClassificationDto | 'new' | null>(null);
  const [maxLevel, setMaxLevel] = useState<number | null>(null);
  const { run, busy } = useAction();

  return (
    <>
      <PageHeader title="Estructura del catálogo" subtitle="Niveles consecutivos y clasificaciones de cuentas de la empresa." />
      <PageHelp page="structure" />
      <div className="acc-grid-2">
        <Card title="Niveles" actions={<Button kind="primary" onClick={() => setLevel('new')}>Nuevo nivel</Button>}>
          <Loadable state={levels}>
            {(d) => (
              <>
                <Table rows={d.levels} rowKey={(l) => l.code} onRowClick={setLevel} columns={[
                  { key: 'c', title: 'Nivel', render: (l) => l.code },
                  { key: 'd', title: 'Descripción', render: (l) => l.description },
                  { key: 'm', title: 'Máx. caracteres', align: 'right', render: (l) => l.maxCharacters ?? '—' },
                  { key: 'n', title: 'Cuentas', align: 'right', render: (l) => l.accountCount },
                ]} />
                <Form onSubmit={() => maxLevel && run(() => accountsApi.setMaxLevel(maxLevel), { success: 'Nivel máximo actualizado.' }).then(levels.reload)} className="acc-inline-form">
                  <Field label={`Nivel máximo de la empresa (actual: ${d.companyMaxLevel ?? 'sin definir'})`}>
                    <NumberInput step="1" min={1} value={maxLevel ?? d.companyMaxLevel ?? null} onChange={setMaxLevel} />
                  </Field>
                  <Button type="submit" busy={busy}>Guardar</Button>
                </Form>
              </>
            )}
          </Loadable>
        </Card>
        <Card title="Clasificaciones" actions={<Button kind="primary" onClick={() => setCls('new')}>Nueva clasificación</Button>}>
          <Alert>La sección define en qué estado financiero aparecen sus cuentas. Las marcadas “deducida” se infieren del nombre; confírmelas para no depender de ello.</Alert>
          <Loadable state={classes}>
            {(rows) => (
              <Table rows={rows} rowKey={(c) => c.code} columns={[
                { key: 'c', title: 'Código', render: (c) => c.code },
                { key: 'd', title: 'Descripción', render: (c) => <Button kind="link" onClick={() => setCls(c)}>{c.description}</Button> },
                { key: 'n', title: 'Cuentas', align: 'right', render: (c) => c.accountCount },
                {
                  key: 's', title: 'Sección', render: (c) => (
                    <span className="acc-row">
                      <Select value={c.statementSection ?? ''} placeholder="Sin sección" options={STATEMENT_SECTIONS.map((s) => ({ value: s, label: s }))}
                        onChange={(s) => run(() => accountsApi.setClassificationSection(c.code, s || null), { success: 'Sección guardada.' }).then(classes.reload)} />
                      {c.statementSectionInferred && c.statementSection && <Badge tone="warn">deducida</Badge>}
                    </span>
                  ),
                },
                { key: 'e', title: 'Estado', render: (c) => <Badge tone={c.status === 'I' ? 'bad' : 'ok'}>{c.status === 'I' ? 'Inactiva' : 'Activa'}</Badge> },
              ]} />
            )}
          </Loadable>
        </Card>
      </div>
      {level && <LevelForm level={level === 'new' ? null : level} onClose={() => setLevel(null)} onSaved={() => { setLevel(null); levels.reload(); }} />}
      {cls && <ClassificationForm cls={cls === 'new' ? null : cls} onClose={() => setCls(null)} onSaved={() => { setCls(null); classes.reload(); }} />}
    </>
  );
}

function LevelForm({ level, onClose, onSaved }: { level: LevelDto | null; onClose: () => void; onSaved: () => void }) {
  const [description, setDescription] = useState(level?.description ?? '');
  const [max, setMax] = useState<number | null>(level?.maxCharacters ?? null);
  const { run, busy } = useAction();
  const dto = { description: description.trim(), maxCharacters: max || null };
  return (
    <Modal title={level ? `Nivel ${level.code}` : 'Nuevo nivel'} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { if (level) await accountsApi.updateLevel(level.code, dto); else await accountsApi.createLevel(dto); onSaved(); }, { success: 'Nivel guardado.' })}>
        <Field label="Descripción"><Input value={description} onChange={setDescription} required maxLength={200} /></Field>
        <Field label="Máximo de caracteres del número de cuenta" hint="Vacío = sin límite"><NumberInput step="1" min={0} value={max} onChange={setMax} /></Field>
        <div className="acc-actions">
          {level && level.accountCount === 0 && <Button kind="danger" busy={busy} onClick={() => run(async () => { await accountsApi.deleteLevel(level.code); onSaved(); }, { confirm: `Eliminar el nivel ${level.code}? Sólo el último nivel puede eliminarse.`, danger: true, success: 'Nivel eliminado.' })}>Eliminar</Button>}
          <Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button>
        </div>
      </Form>
    </Modal>
  );
}

function ClassificationForm({ cls, onClose, onSaved }: { cls: ClassificationDto | null; onClose: () => void; onSaved: () => void }) {
  const [description, setDescription] = useState(cls?.description ?? '');
  const { run, busy } = useAction();
  return (
    <Modal title={cls ? `Clasificación ${cls.code}` : 'Nueva clasificación'} onClose={onClose} size="sm">
      <Form onSubmit={() => run(async () => { if (cls) await accountsApi.updateClassification(cls.code, description.trim()); else await accountsApi.createClassification(description.trim()); onSaved(); }, { success: 'Clasificación guardada.' })}>
        <Field label="Descripción"><Input value={description} onChange={setDescription} required maxLength={200} /></Field>
        <div className="acc-actions">
          {cls && <Button busy={busy} onClick={() => run(async () => { await accountsApi.setClassificationStatus(cls.code, cls.status === 'I'); onSaved(); }, { success: 'Estado actualizado.' })}>{cls.status === 'I' ? 'Activar' : 'Inactivar'}</Button>}
          {cls && cls.accountCount === 0 && <Button kind="danger" busy={busy} onClick={() => run(async () => { await accountsApi.deleteClassification(cls.code); onSaved(); }, { confirm: 'Eliminar la clasificación?', danger: true, success: 'Clasificación eliminada.' })}>Eliminar</Button>}
          <Button onClick={onClose}>Cancelar</Button><Button kind="primary" type="submit" busy={busy}>Guardar</Button>
        </div>
      </Form>
    </Modal>
  );
}
